import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SocialPlatform } from '../types';
import { getPlatformName } from '../config/platforms';
import { adviseOnTime, suggestTimes } from '../config/postingSchedule';

interface Props {
  platforms: SocialPlatform[];
  when: Date | null;
  /** Applies a suggested time. Omit to render advice without the shortcut buttons. */
  onPick?: (date: Date) => void;
}

const formatWhen = (d: Date) =>
  d.toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * Warns when a chosen publish time falls outside the recommended window for one of the
 * selected platforms, and offers times that do fit.
 *
 * Advisory only, and deliberately never blocks: the schedule is a set of heuristics, and the
 * person choosing 3am on a Sunday may well have a reason. It is also frontend-only — the API
 * accepts any time without comment, because a script's schedule is not this page's business.
 *
 * Per platform rather than in aggregate, so a time that suits Facebook but not LinkedIn says
 * exactly that instead of hiding the trade-off behind a single verdict.
 */
export const TimingAdvice: React.FC<Props> = ({ platforms, when, onPick }) => {
  const navigate = useNavigate();
  const advice = adviseOnTime(platforms, when);

  // Nothing selected, no time chosen, or no platform has guidance — say nothing at all
  if (!when || advice.length === 0) return null;

  const outside = advice.filter(a => !a.ok);
  const inside = advice.filter(a => a.ok);

  if (outside.length === 0) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        {formatWhen(when)} is inside the suggested window for{' '}
        {inside.map(a => getPlatformName(a.platform)).join(', ')}.
      </Alert>
    );
  }

  const suggestions = onPick ? suggestTimes(platforms, when) : [];

  return (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Outside the suggested window</AlertTitle>

      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: inside.length || suggestions.length ? 1 : 0 }}>
        {outside.map(a => (
          <Box component="li" key={a.platform}>
            <Typography variant="body2">
              <strong>{getPlatformName(a.platform)}</strong> — better: {a.better}
            </Typography>
          </Box>
        ))}
        {inside.map(a => (
          <Box component="li" key={a.platform}>
            <Typography variant="body2" color="text.secondary">
              {getPlatformName(a.platform)} — this time is fine
            </Typography>
          </Box>
        ))}
      </Box>

      {suggestions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          {suggestions.map(s => (
            <Button
              key={s.when.toISOString()}
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => onPick?.(s.when)}
            >
              {formatWhen(s.when)}
              {platforms.length > 1 && ` · suits ${s.suits}`}
            </Button>
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Only a suggestion — you can publish at this time regardless. The windows are starting
        points, not measurements; see the{' '}
        <Link
          component="button"
          onClick={() => navigate('/schedule')}
          sx={{ verticalAlign: 'baseline', fontSize: 'inherit' }}
        >
          posting schedule
        </Link>
        .
      </Typography>
    </Alert>
  );
};
